import { useRef } from 'react';
import { Maybe } from '@investigator/shared/generated/graphql';

type ApolloQueryHook<TData, TVars> = (options: {
  variables: TVars;
  skip?: boolean;
  onCompleted?: (data: TData) => void;
  onError?: (error: Error) => void;
}) => {
  loading: boolean;
  refetch: (vars?: Partial<TVars>) => Promise<unknown>;
  data?: TData;
};

// We assume poll queries return this shape; if not present, we treat as complete
type PollResultShape = {
  pollQueryJob?: Maybe<{
    cancelled?: Maybe<boolean>;
    done?: Maybe<boolean>;
  }>;
};

interface UseQueryWithRetryOptions<TData extends PollResultShape, TVars> {
  queryHook: ApolloQueryHook<TData, TVars>; // The Apollo query hook to use (e.g., usePollQueryJob)
  variables: TVars;
  skip?: boolean;
  onCompleted?: (data: TData) => void;
  onError?: (error: Error) => void;
  maxRetries?: number; // Default: 5
  retryDelayMs?: number; // Default: 1000ms
}

export const useQueryWithRetry = <TData extends PollResultShape, TVars>({
  queryHook,
  variables,
  skip = false,
  onCompleted,
  onError,
  maxRetries = 5,
  retryDelayMs = 1000,
}: UseQueryWithRetryOptions<TData, TVars>) => {
  const retryCount = useRef(0);
  const { loading, refetch, data } = queryHook({
    variables,
    skip,
    onCompleted: (data: TData) => {
      const cancelled = data.pollQueryJob?.cancelled ?? false;
      const done = data.pollQueryJob?.done ?? false;
      const isCancelled = Boolean(cancelled);
      const isDone = Boolean(done);

      if (!isDone && !isCancelled) {
        if (retryCount.current < maxRetries) {
          retryCount.current += 1;
          setTimeout(() => {
            refetch();
          }, retryDelayMs);
        }
      } else {
        // Reset retry count when query succeeds
        retryCount.current = 0;
        onCompleted?.(data);
      }
    },
    onError: (error: Error) => {
      retryCount.current = 0; // Reset retry count on error
      onError && onError(error);
    },
  });

  return { loading, refetch, data };
};
